import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { Supplier } from '../../types/supplier';
import { supplierApiService } from '../../services/supplierApiService';

// State structure with loading and error
interface SuppliersState {
  suppliers: Supplier[];
  loading: boolean;
  error: string | null;
}

// Initial state
const initialState: SuppliersState = {
  suppliers: [],
  loading: false,
  error: null
};

// Helper function to create a blank supplier
export function createBlankSupplier(): Supplier {
  return {
    id: uuidv4(),
    supplierName: '',
    supplierCode: '',
    contactInformation: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    ingredientsSupplied: [],
    approvalDate: '',
    reviewDate: '',
    approvedBy: '',
    comments: '',
    createdAt: new Date().toISOString(),
    attachments: []
  };
}

// Async thunks

/**
 * Fetch all suppliers from the API
 */
export const fetchSuppliers = createAsyncThunk(
  'suppliers/fetchSuppliers',
  async () => {
    return await supplierApiService.fetchSuppliers();
  }
);

/**
 * Fetch a single supplier by ID
 */
export const fetchSupplierById = createAsyncThunk(
  'suppliers/fetchSupplierById',
  async (id: string) => {
    return await supplierApiService.fetchSupplierById(id);
  }
);

/**
 * Create a new supplier
 */
export const createSupplier = createAsyncThunk(
  'suppliers/createSupplier',
  async (supplier: Supplier) => {
    const clientId = supplier.id; // Store client-side ID
    const newSupplier = await supplierApiService.createSupplier(supplier);
    return { clientId, newSupplier }; // Return both IDs
  }
);

/**
 * Update an existing supplier
 */
export const updateSupplier = createAsyncThunk(
  'suppliers/updateSupplier',
  async (supplier: Supplier) => {
    return await supplierApiService.updateSupplier(supplier);
  }
);

/**
 * Delete a supplier by ID
 */
export const deleteSupplier = createAsyncThunk(
  'suppliers/deleteSupplier',
  async (id: string) => {
    await supplierApiService.deleteSupplier(id);
    return id; // Return id for reducer to remove from state
  }
);

/**
 * Delete all suppliers (for testing/admin)
 */
export const deleteAllSuppliers = createAsyncThunk(
  'suppliers/deleteAllSuppliers',
  async () => {
    await supplierApiService.deleteAllSuppliers();
  }
);

const suppliersSlice = createSlice({
  name: 'suppliers',
  initialState,
  reducers: {
    // Add a supplier to local state without API call (for new suppliers being created)
    addSupplierLocally: (state, action) => {
      state.suppliers.push(action.payload);
    },
    // Remove a supplier from local state without API call (for unsaved new suppliers)
    removeSupplierLocally: (state, action) => {
      state.suppliers = state.suppliers.filter(s => s.id !== action.payload);
    }
  },
  extraReducers: (builder) => {
    // Fetch all suppliers
    builder
      .addCase(fetchSuppliers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSuppliers.fulfilled, (state, action) => {
        state.loading = false;
        state.suppliers = action.payload;
      })
      .addCase(fetchSuppliers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch suppliers';
      });

    // Fetch supplier by ID
    builder
      .addCase(fetchSupplierById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSupplierById.fulfilled, (state, action) => {
        state.loading = false;
        // Update the supplier in the array if it exists, otherwise add it
        const index = state.suppliers.findIndex(s => s.id === action.payload.id);
        if (index !== -1) {
          state.suppliers[index] = action.payload;
        } else {
          state.suppliers.push(action.payload);
        }
      })
      .addCase(fetchSupplierById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch supplier';
      });

    // Create supplier
    builder
      .addCase(createSupplier.fulfilled, (state, action) => {
        // Replace the local supplier (with client ID) with the backend supplier
        const { clientId, newSupplier } = action.payload;
        const index = state.suppliers.findIndex(s => s.id === clientId);
        if (index !== -1) {
          state.suppliers[index] = newSupplier;
        } else {
          // Fallback: if client supplier not found, just add the new one
          state.suppliers.push(newSupplier);
        }
      })
      .addCase(createSupplier.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to create supplier';
      });

    // Update supplier
    builder
      .addCase(updateSupplier.fulfilled, (state, action) => {
        const index = state.suppliers.findIndex(s => s.id === action.payload.id);
        if (index !== -1) {
          state.suppliers[index] = action.payload;
        }
      })
      .addCase(updateSupplier.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update supplier';
      });

    // Delete supplier
    builder
      .addCase(deleteSupplier.fulfilled, (state, action) => {
        state.suppliers = state.suppliers.filter(s => s.id !== action.payload);
      })
      .addCase(deleteSupplier.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to delete supplier';
      });

    // Delete all suppliers
    builder
      .addCase(deleteAllSuppliers.fulfilled, (state) => {
        state.suppliers = [];
      })
      .addCase(deleteAllSuppliers.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to delete all suppliers';
      });
  }
});

export const { addSupplierLocally, removeSupplierLocally } = suppliersSlice.actions;
export default suppliersSlice.reducer;
